import {
    DescriptionEntry,
    ListGroup,
    SelectEntry,
    isSelectEntryEdited,
} from "@bpmn-io/properties-panel";
import { useService } from "bpmn-js-properties-panel";
import { is } from "bpmn-js/lib/util/ModelUtil";

// Import your custom list group entries.
import decisionInputProps from "../input/InputProperties";
import decisionRuleProps from "../rule/RuleProperties";

const LOW_PRIORITY = 500;

/**
 * A provider with a `#getGroups(element)` method
 * that exposes groups for a diagram element.
 *
 * @param {PropertiesPanel} propertiesPanel
 * @param {Function} translate
 */
export default function DecisionPropertiesProvider(
    propertiesPanel,
    injector,
    translate,
    moddle
) {
    /**
     * Return the groups provided for the given element.
     *
     * @param {DiagramElement} element
     *
     * @return {(Object[]) => (Object[])} groups middleware
     */
    this.getGroups = function (element) {
        /**
         * We return a middleware that modifies
         * the existing groups.
         *
         * @param {Object[]} groups
         *
         * @return {Object[]} modified groups
         */
        return function (groups) {
            switch (true) {
                case is(element, "bpmn:BusinessRuleTask"):
                case is(element, "bpmn:Gateway"):
                    groups.push(createGroup(element, injector, translate));
                    break;
            }

            return groups;
        };
    };

    // Register custom properties provider.
    // Use a lower priority to ensure it is loaded after
    // the basic BPMN properties.
    propertiesPanel.registerProvider(LOW_PRIORITY, this);
}

function createGroup(element, injector, translate) {
    switch (element.businessObject.hitPolicy) {
        case "FIRST":
            break;
        case "PRIORITY":
            break;
        default:
            delete element.businessObject["hitPolicy"];
    }

    return {
        id: "decision-group",
        label: translate("Decision"),
        entries: [
            {
                id: "description-header",
                forId: "decision-group",
                value: (
                    <div>
                        <p>
                            A decision table definition based on the
                            Brixx-Decision-Script.
                        </p>
                    </div>
                ),
                component: DecisionDescription,
            },
            {
                id: "hitPolicy",
                component: HitPolicy,
                isEdited: isSelectEntryEdited,
                element,
            },
            {
                id: "input",
                label: translate("Input"),
                component: ListGroup,
                ...decisionInputProps({ element, injector }),
            },
            {
                id: "rules",
                label: translate("Rules"),
                component: ListGroup,
                ...decisionRuleProps({ element, injector }),
            },
            {
                id: "description-footer",
                forId: "decision-group",
                value: (
                    <div>
                        <label class="bio-properties-panel-description">
                            *Required fields
                        </label>
                        <a
                            href="https://github.com/BietSoft/brixx.docs/tree/main/brixx-decision-script#-decision-table-definition"
                            target="_blank"
                            rel="noopener"
                        >
                            {translate("Reference: Decision table definition")}
                        </a>
                    </div>
                ),
                component: DecisionDescription,
            },
        ],
    };
}

function HitPolicy(props) {
    const { id, element } = props;

    const modeling = useService("modeling");
    const translate = useService("translate");
    const debounce = useService("debounceInput");

    const types = [];

    const getValue = () => {
        return element.businessObject.hitPolicy || "";
    };

    const setValue = (value) => {
        return modeling.updateProperties(element, {
            hitPolicy: value,
        });
    };

    const getOptions = () => {
        return [
            { label: translate("<none>"), value: undefined },
            { label: "First", value: "FIRST" },
            { label: "Priority", value: "PRIORITY" },
            ...types.map((type) => ({
                label: type,
                value: type,
            })),
        ];
    };

    const entry = (
        <div class="bio-properties-panel-group">
            <SelectEntry
                id={id}
                element={element}
                label={translate("Hit policy")}
                description={translate(
                    "An optional decision table hit policy."
                )}
                getValue={getValue}
                setValue={setValue}
                getOptions={getOptions}
                debounce={debounce}
                disabled={tryoutButtonsDisabled}
            />
        </div>
    );

    return entry;
}

function DecisionDescription(props) {
    const { element, forId, value } = props;

    const entry = (
        <div style="margin-left:12px; margin-right:30px">
            <DescriptionEntry element={element} forId={forId} value={value} />
        </div>
    );

    return entry;
}

DecisionPropertiesProvider.$inject = [
    "propertiesPanel",
    "injector",
    "translate",
    "moddle",
];
